import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import Head from "next/head";
import Image from "next/image";
import { useRouter } from "next/router";
import type { GetServerSideProps } from "next";
import type { ConfirmationResult } from "firebase/auth";
import { getAccountPageProps, type AccountPageProps } from "@/lib/account-page";
import { describeAuthError } from "@/lib/auth-errors";
import { useFirebaseAuth } from "@/lib/firebase-auth-context";
import { ensureCustomerProfile } from "@/lib/customer-profile";
import { getStorefrontSiteConfig } from "@/lib/storefront-site";
import { normalisePhone } from "@/lib/phone";

export const getServerSideProps: GetServerSideProps = async (context) => {
  return { props: await getAccountPageProps(context, "Sign in") };
};

const RECAPTCHA_CONTAINER_ID = "rl-phone-recaptcha";

export default function SignInPage({ siteCode }: AccountPageProps) {
  const router = useRouter();
  const {
    startPhoneVerification,
    confirmOtp,
    user,
    loading,
    configured,
  } = useFirebaseAuth();
  // Sign-in uses the inverse logo variant per site:
  //   asorockfood   → /logo-desktop-inverse.svg
  //   asorockfrozen → /logo-desktop-frozen-inverse.svg
  const { inverseLogo, brandName } = getStorefrontSiteConfig(siteCode);

  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submittedPhoneRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loading && user) {
      void router.replace("/account");
    }
  }, [loading, user, router]);

  async function handlePhoneSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const e164 = normalisePhone(phone);
    if (!/^\+\d{8,15}$/.test(e164)) {
      setError("Please enter a valid phone number, including the country code.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await startPhoneVerification(e164, RECAPTCHA_CONTAINER_ID);
      setConfirmation(result);
      submittedPhoneRef.current = e164;
      setStep("otp");
    } catch (caught) {
      setError(describeAuthError(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleOtpSubmit(event: FormEvent) {
    event.preventDefault();
    if (!confirmation) {
      setStep("phone");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const signedInUser = await confirmOtp(confirmation, otp);
      try {
        await ensureCustomerProfile(
          signedInUser.uid,
          submittedPhoneRef.current ?? signedInUser.phoneNumber ?? null,
        );
      } catch (profileError) {
        // Don't block sign-in if Firestore rejects the profile seed —
        // the user is signed in and the next save/order capture will
        // surface the failure. Log so it never goes invisible again.
        // eslint-disable-next-line no-console
        console.error("[sign-in] ensureCustomerProfile failed:", profileError);
      }
      await router.replace("/account");
    } catch (caught) {
      setError(describeAuthError(caught));
      setSubmitting(false);
    }
  }

  function handleChangeNumber() {
    setStep("phone");
    setOtp("");
    setConfirmation(null);
    setError(null);
  }

  return (
    <>
      <Head>
        <title>Sign in</title>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <div className="storefront-page-backdrop min-h-[70vh] px-4 py-12 sm:py-16">
        <div className="storefront-page-panel mx-auto w-full max-w-md rounded-[2rem] px-6 py-8 sm:px-8">
          <div className="mb-6 flex justify-center">
            <Image
              src={inverseLogo}
              alt={brandName}
              width={162}
              height={36}
              priority
            />
          </div>
          <p className="storefront-page-overline text-[0.7rem] font-semibold uppercase tracking-[0.26em]">
            Your account
          </p>
          <h1 className="storefront-page-title mt-3 text-[1.9rem] font-bold tracking-tight">
            {step === "phone" ? "Sign in" : "Enter the code"}
          </h1>
          <p className="storefront-page-body mt-2 text-sm leading-6">
            {step === "phone"
              ? "We'll send you a one-time code by SMS. New here? You'll be signed up automatically."
              : `We've sent a 6-digit code to ${submittedPhoneRef.current ?? "your phone"}.`}
          </p>

          {!configured ? (
            <p className="mt-6 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Accounts are not available right now.
            </p>
          ) : step === "phone" ? (
            <form className="mt-6 space-y-4" onSubmit={handlePhoneSubmit}>
              <label className="block">
                <span className="storefront-page-body text-sm font-medium">
                  Mobile number
                </span>
                <input
                  type="tel"
                  required
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+44 7700 900123"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  className="storefront-page-input mt-1 w-full rounded-xl px-4 py-3 text-base"
                />
              </label>

              {error ? (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="storefront-page-button inline-flex min-h-12 w-full items-center justify-center rounded-full px-6 text-base font-semibold disabled:opacity-60"
              >
                {submitting ? "Sending code..." : "Send code"}
              </button>
            </form>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={handleOtpSubmit}>
              <label className="block">
                <span className="storefront-page-body text-sm font-medium">
                  6-digit code
                </span>
                <input
                  type="text"
                  required
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="123456"
                  value={otp}
                  onChange={(event) =>
                    setOtp(event.target.value.replace(/[^0-9]/g, ""))
                  }
                  className="storefront-page-input mt-1 w-full rounded-xl px-4 py-3 text-center text-2xl font-semibold tracking-[0.4em]"
                />
              </label>

              {error ? (
                <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={submitting || otp.length < 6}
                className="storefront-page-button inline-flex min-h-12 w-full items-center justify-center rounded-full px-6 text-base font-semibold disabled:opacity-60"
              >
                {submitting ? "Signing in..." : "Sign in"}
              </button>

              <button
                type="button"
                onClick={handleChangeNumber}
                className="storefront-page-link block w-full text-center text-sm"
              >
                Use a different number
              </button>
            </form>
          )}

          <div id={RECAPTCHA_CONTAINER_ID} />
        </div>
      </div>
    </>
  );
}
